"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import { MediaText, type MediaTextProps } from "./MediaText";
import { cn } from "@/lib/utils/cn";
import { Carousel } from "@/components/ui/carousel/carousel";
import { Button } from "@/components/ui/buttons";
import { GlobalTag, ThemeVariant } from "@/types";
import { ChipFilterGroup } from "../ui/ChipFilterGroup";
import { useGlobalTagFilter } from "@/lib/hooks/useGlobalTagFilter";
import { useIsHydrated } from "@/lib/hooks/useIsHydrated";
import { shuffleArray } from "@/lib/utils";
import { AnimatedCard, AnimatedCardContainer } from "../animations";
import { useTranslations } from "@/lib/i18n/useTranslations";
export type CareerStoryTeaserLayout = "list" | "slider";
export type CareerStoryTeaserItem = Omit<MediaTextProps, "mediaWidth" | "mediaPosition" | "variant"> & {
  globalTags?: GlobalTag[];
};
export interface CareerStoryTeaserProps {
  items: CareerStoryTeaserItem[];
  layout?: CareerStoryTeaserLayout;
  variant?: ThemeVariant;
  className?: string;
  showFilter?: boolean;
  /** Options for "Load More" functionality */
  loadMore?: {
    /** Initial number of items to show */
    initialCount: number;
    /** Number of items to add on each click */
    step: number;
    /** Label for the button */
    buttonLabel?: string;
  };
}

/**
 * CareerStoryTeaser component
 * Renders multiple career stories using the MediaText block with fiveTwelfths width.
 * Supports "list" (alternating media position) and "slider" (always media left) layouts.
 */
export const CareerStoryTeaser: React.FC<CareerStoryTeaserProps> = t0 => {
  const $ = _c(58);
  const {
    items,
    layout: t1,
    variant: t2,
    className: t3,
    showFilter: t4,
    loadMore
  } = t0;
  const layout = t1 === undefined ? "list" : t1;
  const variant = t2 === undefined ? "light" : t2;
  const className = t3 === undefined ? "" : t3;
  const showFilter = t4 === undefined ? false : t4;
  const t = useTranslations();
  const isHydrated = useIsHydrated();
  let t5;
  if ($[0] !== isHydrated || $[1] !== items) {
    t5 = isHydrated ? shuffleArray(items) : items;
    $[0] = isHydrated;
    $[1] = items;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const orderedItems = t5;
  const {
    filteredItems,
    allTags,
    setSelectedTags
  } = useGlobalTagFilter(orderedItems);
  const [visibleCount, setVisibleCount] = useState(loadMore?.initialCount ?? items.length);
  let t6;
  if ($[3] !== filteredItems || $[4] !== visibleCount) {
    t6 = filteredItems.slice(0, visibleCount);
    $[3] = filteredItems;
    $[4] = visibleCount;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const displayedItems = t6;
  const hasMore = visibleCount < filteredItems.length;
  let t7;
  if ($[6] !== items || $[7] !== loadMore) {
    t7 = () => {
      if (loadMore) {
        setVisibleCount(prev => Math.min(prev + loadMore.step, items.length));
      }
    };
    $[6] = items;
    $[7] = loadMore;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleLoadMore = t7;
  let t8;
  if ($[9] !== variant) {
    t8 = item => ({
      ...item,
      variant,
      mediaShape: "square" as const,
      button: item.button ? {
        ...item.button,
        color: variant === "light" ? "yellow" : "purple" as "yellow" | "purple",
        variant: "secondary" as const,
        icon: "arrow-right" as const
      } : undefined
    });
    $[9] = variant;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const getUpdateItemProps = t8;
  let t9;
  if ($[11] !== allTags || $[12] !== items || $[13] !== loadMore?.initialCount || $[14] !== setSelectedTags || $[15] !== showFilter || $[16] !== t || $[17] !== variant) {
    t9 = () => {
      if (!showFilter || allTags.length === 0) {
        return;
      }
      return <div className="container"><ChipFilterGroup variant={variant ?? "light"} label={t.common.selectCategory} tags={allTags} canSelectNone={true} searchParamName="story-filter" onChange={tags => {
          setSelectedTags(tags);
          setVisibleCount(loadMore?.initialCount ?? items.length);
        }} /></div>;
    };
    $[11] = allTags;
    $[12] = items;
    $[13] = loadMore?.initialCount;
    $[14] = setSelectedTags;
    $[15] = showFilter;
    $[16] = t;
    $[17] = variant;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const renderFilter = t9;
  if (layout === "slider") {
    if (!items || items.length === 0) {
      return null;
    }
    let t10;
    if ($[19] !== displayedItems || $[20] !== getUpdateItemProps) {
      let t11;
      if ($[22] !== getUpdateItemProps) {
        t11 = (item_0, index) => <div className="w-screen" key={index}><MediaText {...getUpdateItemProps(item_0)} mediaWidth="fiveTwelfths" mediaPosition="left" /></div>;
        $[22] = getUpdateItemProps;
        $[23] = t11;
      } else {
        t11 = $[23];
      }
      t10 = displayedItems.map(t11);
      $[19] = displayedItems;
      $[20] = getUpdateItemProps;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    const sliderItems = t10;
    let t11;
    if ($[24] !== className) {
      t11 = cn("career-story-slider", className);
      $[24] = className;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== renderFilter) {
      t12 = renderFilter();
      $[26] = renderFilter;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    const t13 = items.length > 1;
    let t14;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = {
        loop: false,
        align: "center"
      };
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    let t15;
    if ($[29] !== sliderItems || $[30] !== t13 || $[31] !== variant) {
      t15 = <Carousel items={sliderItems} variant={variant} showArrows={t13} fullWidth={true} opts={t14} />;
      $[29] = sliderItems;
      $[30] = t13;
      $[31] = variant;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    let t16;
    if ($[33] !== t11 || $[34] !== t12 || $[35] !== t15) {
      t16 = <div className={t11}>{t12}{t15}</div>;
      $[33] = t11;
      $[34] = t12;
      $[35] = t15;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    return t16;
  }
  let t10;
  if ($[37] !== className) {
    t10 = cn("career-story-list flex flex-col gap-16 md:gap-24", className);
    $[37] = className;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== renderFilter) {
    t11 = renderFilter();
    $[39] = renderFilter;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== displayedItems || $[42] !== getUpdateItemProps) {
    let t13;
    if ($[44] !== getUpdateItemProps) {
      t13 = (item_1, index_0) => <AnimatedCard key={`service-card-slide-${String(index_0) + "-" + item_1.headline}`} id={String(index_0) + "-" + item_1.headline}><MediaText key={index_0} {...getUpdateItemProps(item_1)} mediaWidth="fiveTwelfths" mediaPosition={index_0 % 2 === 0 ? "left" : "right"} /></AnimatedCard>;
      $[44] = getUpdateItemProps;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    t12 = displayedItems.map(t13);
    $[41] = displayedItems;
    $[42] = getUpdateItemProps;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[46] !== t12) {
    t13 = <AnimatedCardContainer>{t12}</AnimatedCardContainer>;
    $[46] = t12;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== handleLoadMore || $[49] !== hasMore || $[50] !== loadMore || $[51] !== variant) {
    t14 = loadMore && hasMore && <div className="flex justify-center mt-xl"><Button variant="secondary" color={variant === "light" ? "yellow" : "purple"} onClick={handleLoadMore} label={loadMore.buttonLabel || "Mehr laden"} icon="arrow-right" /></div>;
    $[48] = handleLoadMore;
    $[49] = hasMore;
    $[50] = loadMore;
    $[51] = variant;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t13 || $[56] !== t14) {
    t15 = <div className={t10}>{t11}{t13}{t14}</div>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  return t15;
};